import { FundriseRouteConfig, NavigationGuard } from 'vue-router';
import {
	goToResumeCheckoutStep,
	goToStartCheckoutStep,
	isIneligibleForCheckout,
	isIRA,
	notIRA,
	redirectToQuestionnaire,
	verifyRequiredStates
} from './checkout-state-guards';
import { CheckoutRequiredState } from 'types/checkout';
import { orderForm } from '@store/modules/order-form';
import { questionnaire } from '@store/modules/questionnaire';
import { redirect } from '@utils/client';
import { trackMixpanel } from '@utils/analytics';
import { user } from '@store/modules/user';

export const checkoutGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (!orderForm.dataRetrieved) {
		if (user.user) {
			if (await isIneligibleForCheckout()) {
				redirect('/');
			} else {
				await orderForm.getUserCheckoutDetailsData();
				const notIRACheckout = await notIRA();
				if (notIRACheckout && (!orderForm.plan || !orderForm.serviceLevel)) {
					trackMixpanel('Plan or Service Level Selections Not Initialized', {
						planId: orderForm.plan?.id,
						planName: orderForm.plan?.name,
						serviceLevel: orderForm.serviceLevel
					});
					await redirectToQuestionnaire(next);
				} else {
					next();
				}
			}
		} else {
			if (orderForm.plan === null || orderForm.serviceLevel === null) {
				if (await isIRA()) {
					orderForm.UPDATE_DATA_RETRIEVED(true);
					next();
				} else {
					next({ name: 'home', replace: true });
				}
			} else {
				orderForm.UPDATE_DATA_RETRIEVED(true);
				next();
			}
		}
	} else {
		const hasCompletedQuestionnaire = !!questionnaire.fullResponse.completionDate;
		const hasSelectedPlanServiceLevel = orderForm.plan && orderForm.serviceLevel;
		const isIRACheckout = await isIRA();
		const isGifted = orderForm.isUnclaimedGiftedAccount;
		if (isIRACheckout || hasCompletedQuestionnaire || hasSelectedPlanServiceLevel || isGifted) {
			next();
		} else {
			await redirectToQuestionnaire(next);
		}
	}
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const checkoutRoutes: FundriseRouteConfig[] = [
	{
		path: `/reits/checkout`,
		name: 'checkout',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: checkoutGuard,
		component: () => import(/* webpackChunkName: "checkout" */ '@views/checkout/checkout.vue'),
		children: [
			{
				path: 'start',
				name: 'checkout-start',
				meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
				beforeEnter: async (to, from, next) => {
					await goToStartCheckoutStep(next);
				}
			},
			{
				path: 'resume',
				name: 'checkout-resume',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					await goToResumeCheckoutStep(next);
				}
			},
			{
				path: `/reits/signup-checkout/signup`,
				name: 'checkout-signup',
				meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = ['userNotCreated'];
					await verifyRequiredStates(next, requiredStates);
				},
				component: () => import(/* webpackChunkName: "checkout-signup" */ '@views/checkout/checkout-signup.vue')
			},
			{
				path: `select-country`,
				name: 'checkout-select-country',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceInvalidOrNotSubmitted'
					];
					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-select-country" */ '@views/checkout/checkout-select-country.vue'
					)
			},
			{
				path: `select-account-form`,
				name: 'checkout-account-type',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'notIRA',
						'notGifted'
					];
					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(/* webpackChunkName: "checkout-account-type" */ '@views/checkout/checkout-account-type.vue')
			},
			{
				path: `individual-profile-form`,
				name: 'checkout-individual-account-details',
				meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = ['userCreated', 'residenceValid', 'notIRA'];
					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-individual-account-details" */ '@src/views/checkout/checkout-individual-account-details.vue'
					)
			},
			{
				path: 'joint-account-profile-form',
				name: 'checkout-joint-account-details',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'notIRA',
						'notGifted'
					];
					await verifyRequiredStates(next, requiredStates);
					if (from.name === 'checkout-payment-amount') {
						next({ name: 'checkout-joint-account-details', query: { account: 'update' } });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-joint-account-details" */ '@src/views/checkout/checkout-joint-account-details.vue'
					)
			},
			{
				path: `entity-profile-form`,
				name: 'checkout-entity-account-details',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'notIRA',
						'notGifted'
					];
					await verifyRequiredStates(next, requiredStates);
					if (from.name === 'checkout-payment-amount') {
						next({ name: 'checkout-entity-account-details', query: { account: 'update' } });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-entity-account-details" */ '@src/views/checkout/checkout-entity-account-details.vue'
					)
			},
			{
				path: 'trust-profile-form',
				name: 'checkout-trust-account-details',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'notIRA',
						'notGifted'
					];
					await verifyRequiredStates(next, requiredStates);
					if (from.name === 'checkout-payment-amount') {
						next({ name: 'checkout-trust-account-details', query: { account: 'update' } });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-trust-account-details" */ '@src/views/checkout/checkout-trust-account-details.vue'
					)
			},
			{
				path: `tax-reporting-form`,
				name: 'checkout-tax-reporting-details',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = ['userCreated', 'residenceValid', 'notIRA'];
					await verifyRequiredStates(next, requiredStates);

					const hasSubmittedTaxInfo = orderForm.hasSubmittedSsn && orderForm.hasSubmittedDob;
					if (!hasSubmittedTaxInfo) {
						next();
					} else {
						switch (from.name) {
							case 'checkout-individual-account-details':
								next({ name: 'checkout-payment-amount' });
								break;
							case 'checkout-payment-amount':
							default:
								next({ name: 'checkout-individual-account-details' });
								break;
						}
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-tax-reporting-details" */ '@src/views/checkout/checkout-tax-reporting-details.vue'
					)
			},
			{
				path: `payment-amount`,
				name: 'checkout-payment-amount',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'accountDetailsSubmitted',
						'notIRA',
						'notGifted'
					];
					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-payment-amount" */ '@src/views/checkout/checkout-payment-amount.vue'
					)
			},
			{
				path: `auto-invest`,
				name: 'checkout-auto-invest',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'accountDetailsSubmitted',
						'notIRA',
						'orderGroupCreated'
					];

					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-auto-invest" */ '@src/views/checkout/checkout-auto-invest.vue'
					)
			},
			{
				path: `select-payment-method`,
				name: 'checkout-payment-method',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'accountDetailsSubmitted',
						'orderGroupCreated'
					];

					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-payment-method" */ '@src/views/checkout/checkout-payment-method.vue'
					)
			},
			{
				path: 'new-ach-account',
				name: 'checkout-new-bank-account',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const requiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'accountDetailsSubmitted',
						'orderGroupCreated'
					];

					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-new-bank-account" */ '@src/views/checkout/checkout-new-bank-account.vue'
					)
			},
			{
				path: 'acknowledgments',
				name: 'checkout-acknowledgments',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const giftedAccountRequiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'accountDetailsSubmitted'
					];

					const requiredStates: Array<CheckoutRequiredState> = orderForm.isUnclaimedGiftedAccount
						? giftedAccountRequiredStates
						: [...giftedAccountRequiredStates, 'orderGroupCreated', 'paymentMethodAdded'];

					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-acknowledgments" */ '@src/views/checkout/checkout-acknowledgments.vue'
					)
			},
			{
				path: 'review-order',
				name: 'checkout-review-order',
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				beforeEnter: async (to, from, next) => {
					const giftedAccountRequiredStates: Array<CheckoutRequiredState> = [
						'userCreated',
						'residenceValid',
						'accountDetailsSubmitted',
						'acknowledgmentsAccepted'
					];

					const requiredStates: Array<CheckoutRequiredState> = orderForm.isUnclaimedGiftedAccount
						? giftedAccountRequiredStates
						: [...giftedAccountRequiredStates, 'orderGroupCreated', 'paymentMethodAdded'];

					await verifyRequiredStates(next, requiredStates);
				},
				component: () =>
					import(
						/* webpackChunkName: "checkout-review-order" */ '@src/views/checkout/checkout-review-order.vue'
					)
			}
		]
	},
	{
		path: `/reits/checkout/country-not-supported`,
		name: 'checkout-country-not-supported',
		meta: { minTokenScopeRequired: 'UNVERIFIED' },
		component: () =>
			import(
				/* webpackChunkName: "checkout-country-not-supported" */ '@views/checkout/checkout-country-not-supported.vue'
			)
	}
];

export default checkoutRoutes;
